import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, ArrowDown, TrendingDown, Activity, AlertTriangle, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

import {
  fetchStatus,
  fetchLatest,
  fetchDates,
  fetchByDate,
  triggerScanAsync,
} from "@/lib/api";

import StatusBar from "@/components/StatusBar";
import KpiCards from "@/components/KpiCards";
import LosersTable from "@/components/LosersTable";
import HistoricalPanel from "@/components/HistoricalPanel";
import StockDetailSheet from "@/components/StockDetailSheet";
import EmptyState from "@/components/EmptyState";
import WatchlistPanel from "@/components/WatchlistPanel";

export default function Dashboard() {
  const qc = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(null); // null = latest
  const [activeLoser, setActiveLoser] = useState(null); // {ticker, change_pct, ...}
  const [uiThreshold, setUiThreshold] = useState(() => {
    const v = Number(localStorage.getItem("ui_threshold"));
    return Number.isFinite(v) && v < 0 ? v : -10;
  });
  useEffect(() => {
    localStorage.setItem("ui_threshold", String(uiThreshold));
  }, [uiThreshold]);

  const statusQ = useQuery({
    queryKey: ["status"],
    queryFn: fetchStatus,
    refetchInterval: (q) => (q.state.data?.is_scanning ? 3000 : 15000),
  });

  const datesQ = useQuery({ queryKey: ["dates"], queryFn: fetchDates });

  const snapshotQ = useQuery({
    queryKey: ["snapshot", selectedDate || "latest"],
    queryFn: () => (selectedDate ? fetchByDate(selectedDate) : fetchLatest()),
    retry: false,
  });

  const scanMut = useMutation({
    mutationFn: triggerScanAsync,
    onMutate: () => toast.loading("Scan started — scanning ASX 300…", { id: "scan" }),
    onSuccess: () => {
      toast.success("Scan running in background. Results will refresh automatically.", { id: "scan" });
      qc.invalidateQueries({ queryKey: ["status"] });
    },
    onError: (e) => {
      const msg = e?.response?.data?.detail || e.message || "Scan failed";
      toast.error(msg, { id: "scan" });
    },
  });

  // When a background scan finishes, refresh the snapshot + dates.
  const wasScanning = useRef(false);
  useEffect(() => {
    const scanning = !!statusQ.data?.is_scanning;
    if (wasScanning.current && !scanning) {
      qc.invalidateQueries({ queryKey: ["snapshot"] });
      qc.invalidateQueries({ queryKey: ["dates"] });
      toast.success(
        `Scan complete — ${statusQ.data?.last_scan_losers_count ?? 0} losers found`,
        { id: "scan" }
      );
      setSelectedDate(null);
    }
    wasScanning.current = scanning;
  }, [statusQ.data?.is_scanning, statusQ.data?.last_scan_losers_count, qc]);

  const filteredSnapshot = useMemo(() => {
    if (!snapshotQ.data) return null;
    const filtered = (snapshotQ.data.losers || []).filter(
      (l) => l.change_pct <= uiThreshold
    );
    return { ...snapshotQ.data, losers: filtered };
  }, [snapshotQ.data, uiThreshold]);

  const isEmpty = filteredSnapshot && filteredSnapshot.losers.length === 0;
  const noData = snapshotQ.isError && snapshotQ.error?.response?.status === 404;

  return (
    <div className="grain-bg relative min-h-screen">
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-md bg-[#FF3B30]/15 border border-[#FF3B30]/40 grid place-items-center">
                <ArrowDown className="h-4 w-4 text-[#FF3B30]" />
              </div>
              <span className="text-xs font-mono uppercase tracking-[0.24em] text-zinc-400">
                ASX Losers Dashboard
              </span>
            </div>
            <h1
              data-testid="page-title"
              className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tighter"
            >
              End-of-day <span className="text-[#FF3B30]">{uiThreshold.toFixed(0)}%</span> movers
            </h1>
            <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
              Every trading day at 4:00 PM AEST we scan the ASX 300 and surface
              stocks that closed down 10% or more from the previous session.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="threshold-badge"
                  className="inline-flex items-center rounded-md border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] font-mono text-xs px-3 py-1.5 hover:bg-[#FF3B30]/15"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
                  {uiThreshold.toFixed(0)}% THRESHOLD
                </button>
              </PopoverTrigger>
              <PopoverContent
                data-testid="threshold-popover"
                align="end"
                className="w-72 bg-[#121214] border border-white/10 text-zinc-100"
              >
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                  Display filter
                </div>
                <p className="mt-1 text-xs text-zinc-400">
                  Server scans at −10%. Adjust the local filter to see smaller
                  moves in the table.
                </p>
                <div className="mt-4 flex items-baseline justify-between font-mono">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Show losers ≤
                  </span>
                  <span className="text-lg text-[#FF6B62]">
                    {uiThreshold.toFixed(0)}%
                  </span>
                </div>
                <Slider
                  data-testid="threshold-slider"
                  min={-30}
                  max={-3}
                  step={1}
                  value={[uiThreshold]}
                  onValueChange={([v]) => setUiThreshold(v)}
                  className="mt-3"
                />
              </PopoverContent>
            </Popover>
            <Button
              data-testid="refresh-now-button"
              onClick={() => scanMut.mutate()}
              disabled={scanMut.isPending || statusQ.data?.is_scanning}
              className="bg-[#FF3B30] hover:bg-[#e0342b] text-white font-mono text-xs uppercase tracking-widest rounded-md px-4"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${scanMut.isPending ? "animate-spin" : ""}`} />
              {scanMut.isPending ? "Scanning…" : "Refresh Now"}
            </Button>
          </div>
        </header>

        <StatusBar status={statusQ.data} isLoading={statusQ.isLoading} />

        <div className="mt-6">
          <KpiCards snapshot={filteredSnapshot} isLoading={snapshotQ.isLoading} />
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="bg-[#121214] border-white/10 rounded-md overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <div>
                  <h2 className="text-lg font-bold tracking-tight">
                    {selectedDate ? "Historical losers" : "Today's losers"}
                  </h2>
                  <p className="text-xs font-mono text-zinc-500 mt-1">
                    {snapshotQ.data
                      ? `Session ${snapshotQ.data.date} · ${snapshotQ.data.scanned_count} scanned`
                      : "Awaiting scan"}
                  </p>
                </div>
                {selectedDate && (
                  <Button
                    data-testid="clear-date-filter"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedDate(null)}
                    className="text-xs font-mono text-zinc-400 hover:text-white"
                  >
                    Show latest
                  </Button>
                )}
              </div>

              {snapshotQ.isLoading && (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-10 rounded bg-zinc-800/60 animate-pulse" />
                  ))}
                </div>
              )}

              {noData && (
                <EmptyState
                  title="No snapshots yet"
                  message="Run a scan to fetch today's ASX 300 losers."
                  action={
                    <Button
                      data-testid="empty-run-scan"
                      onClick={() => scanMut.mutate()}
                      disabled={scanMut.isPending}
                      className="bg-[#FF3B30] hover:bg-[#e0342b] text-white font-mono text-xs uppercase tracking-widest"
                    >
                      Run first scan
                    </Button>
                  }
                />
              )}

              {filteredSnapshot && !isEmpty && (
                <LosersTable
                  losers={filteredSnapshot.losers}
                  onRowClick={(l) => setActiveLoser(l)}
                />
              )}

              {filteredSnapshot && isEmpty && (
                <EmptyState
                  title={
                    uiThreshold === -10
                      ? "No stocks crashed today"
                      : `No stocks below ${uiThreshold}%`
                  }
                  message={
                    uiThreshold === -10
                      ? `No ASX 300 stocks closed down 10% or more on ${filteredSnapshot.date}.`
                      : `Nothing matches your current display filter. Server always scans at −10%; only stocks that met that bar are in this snapshot.`
                  }
                  image="https://images.pexels.com/photos/25470043/pexels-photo-25470043.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                />
              )}
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <HistoricalPanel
              dates={datesQ.data || []}
              selectedDate={selectedDate}
              onSelect={setSelectedDate}
              isLoading={datesQ.isLoading}
            />
            <WatchlistPanel />
          </div>
        </div>

        <footer className="mt-12 pt-6 border-t border-white/5 text-xs font-mono text-zinc-500 flex flex-wrap gap-x-6 gap-y-2">
          <span>Data: Yahoo Finance</span>
          <span>Universe: ASX 300</span>
          <span>Scheduled: 16:15 AEST</span>
          <span>Threshold: −10.00%</span>
        </footer>
      </div>

      <StockDetailSheet
        loser={activeLoser}
        open={!!activeLoser}
        onOpenChange={(o) => !o && setActiveLoser(null)}
      />
    </div>
  );
}
